/**
 * Individual display rating tiers.
 * Six tiers with DYNAMIC thresholds: the backend recomputes the five cutoffs
 * from the active pool's mean/σ on every stats refresh and ships them in
 * /api/current-user/ (fetched at app boot, before any page renders). The
 * defaults below only cover the moment before that response lands and match
 * the backend's fallback for a too-small pool.
 */
const DEFAULT_INDIVIDUAL_TIERS = [
  { name: 'Legendary Grandmaster', color: '#DC2626', min: 1565 },
  { name: 'Grandmaster',           color: '#EF4444', min: 1540 },
  { name: 'Master',                color: '#EAB308', min: 1515 },
  { name: 'Expert',                color: '#3B82F6', min: 1485 },
  { name: 'Pupil',                 color: '#22C55E', min: 1455 },
  { name: 'Newbie',                color: '#6B7280', min: null },
];

let individualTiers = DEFAULT_INDIVIDUAL_TIERS;

/** Called from App boot with the server's live thresholds. */
export function setIndividualTiers(tiers) {
  if (Array.isArray(tiers) && tiers.length >= 2 &&
      tiers.every(t => t && typeof t.name === 'string' && typeof t.color === 'string')) {
    individualTiers = tiers;
  }
}

function tierFor(rating) {
  const r = Math.round(rating);
  for (const t of individualTiers) {
    if (t.min === null || t.min === undefined || r >= t.min) return t;
  }
  return individualTiers[individualTiers.length - 1];
}

export function getRatingColor(rating) {
  return tierFor(rating).color;
}

export function getRatingTier(rating) {
  return tierFor(rating).name;
}

export function getRatingRanges() {
  return individualTiers.map((t, i) => {
    const min = (t.min === null || t.min === undefined) ? 0 : t.min;
    const max = i === 0 ? Infinity : individualTiers[i - 1].min - 1;
    const label = i === 0 ? `${min}+`
      : (t.min === null || t.min === undefined) ? `<${individualTiers[i - 1].min}`
      : `${min}-${max}`;
    return { min, max, color: t.color, name: t.name, label };
  });
}

export function getRatingColorGradient(elo, opacity = 0.2) {
  const color = getRatingColor(elo);
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function getFormattedTierName(elo) {
  const t = tierFor(elo);
  if (t.name === 'Legendary Grandmaster') {
    return (
      <span>
        <span style={{ color: '#000000' }}>L</span>
        <span style={{ color: t.color }}>egendary Grandmaster</span>
      </span>
    );
  }
  return t.name;
}

/**
 * Team display rating tiers (base 1500).
 * Wider bands than individual since team Elo has more variance.
 * IGM (1700-1749) and Legendary (1750+) are aspirational.
 */
export function getTeamRatingTier(rating) {
  const r = Math.round(rating);
  if (r >= 1750) return 'Legendary Grandmaster';
  if (r >= 1700) return 'International Grandmaster';
  if (r >= 1600) return 'Grandmaster';
  if (r >= 1550) return 'International Master';
  if (r >= 1500) return 'Master';
  if (r >= 1450) return 'Candidate Master';
  if (r >= 1400) return 'Expert';
  if (r >= 1350) return 'Specialist';
  if (r >= 1300) return 'Pupil';
  return 'Newbie';
}

export function getTeamRatingColor(rating) {
  const r = Math.round(rating);
  if (r >= 1750) return '#991B1B';
  if (r >= 1700) return '#B91C1C';
  if (r >= 1600) return '#EF4444';
  if (r >= 1550) return '#F97316';
  if (r >= 1500) return '#EAB308';
  if (r >= 1450) return '#6D28D9';
  if (r >= 1400) return '#3B82F6';
  if (r >= 1350) return '#06B6D4';
  if (r >= 1300) return '#22C55E';
  return '#6B7280';
}

export function getTeamRatingRanges() {
  return [
    { min: 1750, max: Infinity, color: '#991B1B', name: 'Legendary Grandmaster', label: '1750+' },
    { min: 1700, max: 1749, color: '#B91C1C', name: 'International Grandmaster', label: '1700-1749' },
    { min: 1600, max: 1699, color: '#EF4444', name: 'Grandmaster', label: '1600-1699' },
    { min: 1550, max: 1599, color: '#F97316', name: 'International Master', label: '1550-1599' },
    { min: 1500, max: 1549, color: '#EAB308', name: 'Master', label: '1500-1549' },
    { min: 1450, max: 1499, color: '#6D28D9', name: 'Candidate Master', label: '1450-1499' },
    { min: 1400, max: 1449, color: '#3B82F6', name: 'Expert', label: '1400-1449' },
    { min: 1350, max: 1399, color: '#06B6D4', name: 'Specialist', label: '1350-1399' },
    { min: 1300, max: 1349, color: '#22C55E', name: 'Pupil', label: '1300-1349' },
    { min: 0,    max: 1299, color: '#6B7280', name: 'Newbie', label: '<1300' },
  ];
}

export function getBondingColor(score) {
  if (score >= 50)  return '#DC2626';
  if (score >= 30)  return '#F97316';
  if (score >= 20)  return '#EAB308';
  if (score >= 5)   return '#6D28D9';
  if (score >= 0)   return '#3B82F6';
  if (score >= -20) return '#06B6D4';
  if (score >= -35) return '#22C55E';
  return '#6B7280';
}

export function getFormattedTeamTierName(elo) {
  const tier = getTeamRatingTier(elo);
  if (tier === 'Legendary Grandmaster') {
    return (
      <span>
        <span style={{ color: '#000000' }}>L</span>
        <span style={{ color: '#991B1B' }}>egendary Grandmaster</span>
      </span>
    );
  }
  return tier;
}
